import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, User, Settings, CreditCard, Bell, LogOut, ChevronRight, ChevronDown,
  Mail, Phone, Calendar, MapPin, Loader2, Check, Dumbbell, Utensils, Clock, X, Trophy, RefreshCw, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { fetchApi, formatDate, EQUIPMENT, ALLERGIES, DIETARY_PREFERENCES, TRAINING_EVENTS, CUISINES, GOALS } from '../lib/utils';

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  
  const [expandedSection, setExpandedSection] = useState(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    country: user?.country || '',
  });
  
  const [trainingPrefs, setTrainingPrefs] = useState({
    days_per_week: 3,
    time_per_session: 45,
    experience_level: 'beginner',
    training_location: 'gym',
    equipment: [],
    injury_history: '',
    current_limitations: '',
    target_event: 'none',
    event_date: '',
  });
  
  const [goalsPrefs, setGoalsPrefs] = useState({
    selected_goals: [],
    custom_goal: '',
  });
  
  const [nutritionPrefs, setNutritionPrefs] = useState({
    dietary_preference: '',
    allergies: [],
    meals_per_day: 3,
    cooking_time: 'moderate',
    budget: 'moderate',
    foods_to_avoid: [],
    disliked_foods: [],
    disliked_ingredients: [],
    preferred_cuisines: [],
  });
  
  const [regeneratingTraining, setRegeneratingTraining] = useState(false);
  const [regeneratingNutrition, setRegeneratingNutrition] = useState(false);
  const [newDislikedFood, setNewDislikedFood] = useState('');
  const [newDislikedIngredient, setNewDislikedIngredient] = useState('');
  
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    inactivity: true,
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const data = await fetchApi('/preferences');
      setPreferences(data);
      
      setTrainingPrefs({
        days_per_week: data.training?.days_per_week || 3,
        time_per_session: data.training?.time_per_session || 45,
        experience_level: data.training?.experience_level || 'beginner',
        training_location: data.environment?.training_location || 'gym',
        equipment: data.environment?.equipment || [],
        injury_history: data.training?.injury_history || '',
        current_limitations: data.training?.current_limitations || '',
        target_event: data.event?.target_event || 'none',
        event_date: data.event?.event_date || '',
      });
      
      setGoalsPrefs({
        selected_goals: data.goals?.selected_goals || [],
        custom_goal: data.goals?.custom_goal || '',
      });
      
      setNutritionPrefs({
        dietary_preference: data.nutrition?.dietary_preference || '',
        allergies: data.nutrition?.allergies || [],
        meals_per_day: data.nutrition?.meals_per_day || 3,
        cooking_time: data.nutrition?.cooking_time || 'moderate',
        budget: data.nutrition?.budget || 'moderate',
        foods_to_avoid: data.nutrition?.foods_to_avoid || [],
        disliked_foods: data.nutrition?.disliked_foods || [],
        disliked_ingredients: data.nutrition?.disliked_ingredients || [],
        preferred_cuisines: data.nutrition?.preferred_cuisines || [],
      });
    } catch (error) {
      console.error('Load preferences error:', error);
    } finally {
      setLoadingPrefs(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await fetchApi('/onboarding/profile', {
        method: 'PUT',
        body: JSON.stringify({
          ...formData,
          email: user.email,
        }),
      });
      updateUser(formData);
      setEditing(false);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTraining = async (regenerate = false) => {
    setSaving(true);
    try {
      // Save training preferences
      await fetchApi('/preferences/training', {
        method: 'PUT',
        body: JSON.stringify({
          days_per_week: trainingPrefs.days_per_week,
          time_per_session: trainingPrefs.time_per_session,
          experience_level: trainingPrefs.experience_level,
          injury_history: trainingPrefs.injury_history,
          current_limitations: trainingPrefs.current_limitations,
        }),
      });
      
      // Save event preferences
      await fetchApi('/onboarding/event', {
        method: 'PUT',
        body: JSON.stringify({
          target_event: trainingPrefs.target_event,
          event_date: trainingPrefs.event_date || null,
        }),
      });
      
      // Save environment (location & equipment)
      await fetchApi('/preferences/environment', {
        method: 'PUT',
        body: JSON.stringify({
          training_location: trainingPrefs.training_location,
          equipment: trainingPrefs.equipment,
        }),
      });
      
      if (regenerate) {
        setRegeneratingTraining(true);
        setSaving(false);
        try {
          const result = await fetchApi('/programs/generate', { method: 'POST' });
          if (result && result.program) {
            alert('New training program generated based on your updated preferences!');
          } else {
            alert('Preferences saved! Your new program is being created.');
          }
        } catch (err) {
          const errorMsg = err.message || 'Unknown error';
          if (errorMsg.includes('subscription') || errorMsg.includes('payment')) {
            alert('Preferences saved! To generate a new program, please ensure your subscription is active.');
          } else {
            alert('Preferences saved! Program generation encountered an issue. Please try again from the Training page.');
          }
          console.error('Program generation error:', err);
        } finally {
          setRegeneratingTraining(false);
        }
      } else {
        alert('Training preferences saved successfully!');
      }
      
      setExpandedSection(null);
    } catch (error) {
      console.error('Save training error:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGoals = async (regenerate = false) => {
    setSaving(true);
    try {
      // Ensure we have clean arrays and strings
      const goalsData = {
        selected_goals: Array.isArray(goalsPrefs.selected_goals) 
          ? [...goalsPrefs.selected_goals] 
          : [],
        custom_goal: String(goalsPrefs.custom_goal || ''),
      };
      
      await fetchApi('/preferences/goals', {
        method: 'PUT',
        body: JSON.stringify(goalsData),
      });
      
      if (regenerate) {
        setRegeneratingTraining(true);
        setSaving(false);
        try {
          const result = await fetchApi('/programs/generate', { method: 'POST' });
          if (result && result.program) {
            alert('New training program generated based on your updated goals!');
          } else {
            alert('Goals saved! Your new program is being created.');
          }
        } catch (err) {
          const errorMsg = err.message || 'Unknown error';
          if (errorMsg.includes('subscription') || errorMsg.includes('payment')) {
            alert('Goals saved! To generate a new program, please ensure your subscription is active.');
          } else {
            alert('Goals saved! Program generation encountered an issue. Please try again from the Training page.');
          }
          console.error('Program generation error:', err);
        } finally {
          setRegeneratingTraining(false);
        }
      } else {
        alert('Goals saved successfully!');
      }
      
      setExpandedSection(null);
    } catch (error) {
      console.error('Save goals error:', error);
      alert('Failed to save goals. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleGoal = (goalId) => {
    setGoalsPrefs(prev => {
      const currentGoals = Array.isArray(prev.selected_goals) ? prev.selected_goals : [];
      const newGoals = currentGoals.includes(goalId)
        ? currentGoals.filter(g => g !== goalId)
        : [...currentGoals, goalId];
      return {
        ...prev,
        selected_goals: newGoals
      };
    });
  };

  const handleSaveNutrition = async (regenerate = false) => {
    setSaving(true);
    try {
      await fetchApi('/preferences/nutrition', {
        method: 'PUT',
        body: JSON.stringify({
          ...nutritionPrefs,
          preferred_cuisines: nutritionPrefs.preferred_cuisines,
        }),
      });
      
      if (regenerate) {
        setRegeneratingNutrition(true);
        setSaving(false);
        try {
          await fetchApi('/nutrition/generate', { method: 'POST' });
          alert('New meal plan generated based on your updated preferences!');
        } catch (err) {
          alert('Preferences saved. Meal plan regeneration failed: ' + (err.message || 'Please try again.'));
        } finally {
          setRegeneratingNutrition(false);
        }
      }
      
      setExpandedSection(null);
    } catch (error) {
      console.error('Save nutrition error:', error);
      alert('Failed to save: ' + (error.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleEquipment = (item) => {
    setTrainingPrefs(prev => ({
      ...prev,
      equipment: prev.equipment.includes(item)
        ? prev.equipment.filter(e => e !== item)
        : [...prev.equipment, item]
    }));
  };

  const toggleAllergy = (item) => {
    setNutritionPrefs(prev => ({
      ...prev,
      allergies: prev.allergies.includes(item)
        ? prev.allergies.filter(a => a !== item)
        : [...prev.allergies, item]
    }));
  };

  const addDislikedFood = () => {
    if (newDislikedFood.trim()) {
      setNutritionPrefs(prev => ({
        ...prev,
        disliked_foods: [...prev.disliked_foods, newDislikedFood.trim()]
      }));
      setNewDislikedFood('');
    }
  };

  const removeDislikedFood = (food) => {
    setNutritionPrefs(prev => ({
      ...prev,
      disliked_foods: prev.disliked_foods.filter(f => f !== food)
    }));
  };

  const addDislikedIngredient = () => {
    if (newDislikedIngredient.trim()) {
      setNutritionPrefs(prev => ({
        ...prev,
        disliked_ingredients: [...prev.disliked_ingredients, newDislikedIngredient.trim()]
      }));
      setNewDislikedIngredient('');
    }
  };

  const removeDislikedIngredient = (ingredient) => {
    setNutritionPrefs(prev => ({
      ...prev,
      disliked_ingredients: prev.disliked_ingredients.filter(i => i !== ingredient)
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="text-lg font-bold">Profile & Settings</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-default mb-6"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black text-2xl">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-bold">{user?.name}</h2>
                <p className="text-zinc-400">{user?.email}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Member since {formatDate(user?.created_at)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => setEditing(!editing)}
              className="text-zinc-400"
              data-testid="edit-btn"
            >
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>

          {editing ? (
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-zinc-900 border-zinc-800"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-2 bg-zinc-900 border-zinc-800"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="mt-2 bg-zinc-900 border-zinc-800"
                  data-testid="country-input"
                />
              </div>
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                data-testid="save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {user?.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  <span>{user.phone}</span>
                </div>
              )}
              {user?.country && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-zinc-500" />
                  <span>{user.country}</span>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Training Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-default mb-6"
        >
          <button
            onClick={() => setExpandedSection(expandedSection === 'training' ? null : 'training')}
            className="w-full flex items-center justify-between"
            data-testid="training-prefs-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-[#CCFF00]" />
              </div>
              <div className="text-left">
                <h3 className="font-bold">Training Preferences</h3>
                <p className="text-sm text-zinc-400">
                  {trainingPrefs.days_per_week} days/week • {trainingPrefs.time_per_session} min sessions
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${expandedSection === 'training' ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSection === 'training' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="mt-6 pt-6 border-t border-zinc-800 space-y-6"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Days Per Week</Label>
                  <Select
                    value={String(trainingPrefs.days_per_week)}
                    onValueChange={(v) => setTrainingPrefs({ ...trainingPrefs, days_per_week: parseInt(v) })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800" data-testid="days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} days</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Session Duration</Label>
                  <Select
                    value={String(trainingPrefs.time_per_session)}
                    onValueChange={(v) => setTrainingPrefs({ ...trainingPrefs, time_per_session: parseInt(v) })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800" data-testid="time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60, 75, 90, 120].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} minutes</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Training Location</Label>
                <Select
                  value={trainingPrefs.training_location}
                  onValueChange={(v) => setTrainingPrefs({ ...trainingPrefs, training_location: v })}
                >
                  <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800" data-testid="location-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gym">Gym</SelectItem>
                    <SelectItem value="home_none">Home (No Equipment)</SelectItem>
                    <SelectItem value="home_some">Home (Some Equipment)</SelectItem>
                    <SelectItem value="outdoors">Outdoors</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-zinc-300 mb-3 block">Available Equipment</Label>
                <div className="grid grid-cols-2 gap-2">
                  {EQUIPMENT.map(item => (
                    <div
                      key={item.id}
                      onClick={() => toggleEquipment(item.id)}
                      className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-2 ${
                        trainingPrefs.equipment.includes(item.id)
                          ? 'bg-[#CCFF00]/10 border border-[#CCFF00]/30'
                          : 'bg-zinc-900 hover:bg-zinc-800'
                      }`}
                    >
                      <Checkbox checked={trainingPrefs.equipment.includes(item.id)} />
                      <span className="text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Injury History</Label>
                <Input
                  value={trainingPrefs.injury_history}
                  onChange={(e) => setTrainingPrefs({ ...trainingPrefs, injury_history: e.target.value })}
                  placeholder="e.g., Previous knee injury"
                  className="mt-2 bg-zinc-900 border-zinc-800"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Current Limitations</Label>
                <Input
                  value={trainingPrefs.current_limitations}
                  onChange={(e) => setTrainingPrefs({ ...trainingPrefs, current_limitations: e.target.value })}
                  placeholder="e.g., Lower back pain"
                  className="mt-2 bg-zinc-900 border-zinc-800"
                />
              </div>

              {/* Event Training Selection */}
              <div className="pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-[#CCFF00]" />
                  <Label className="text-zinc-300 font-bold">Event-Specific Training</Label>
                </div>
                <Select
                  value={trainingPrefs.target_event}
                  onValueChange={(v) => setTrainingPrefs({ ...trainingPrefs, target_event: v, event_date: v === 'none' ? '' : trainingPrefs.event_date })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="event-select">
                    <SelectValue placeholder="Select an event" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TRAINING_EVENTS.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.icon} {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {trainingPrefs.target_event && trainingPrefs.target_event !== 'none' && (
                  <div className="mt-4">
                    <Label className="text-zinc-300">Competition Date</Label>
                    <Input
                      type="date"
                      value={trainingPrefs.event_date}
                      onChange={(e) => setTrainingPrefs({ ...trainingPrefs, event_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-2 bg-zinc-900 border-zinc-800"
                      data-testid="event-date-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Your training will be periodized to peak on this date</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleSaveTraining(false)}
                  disabled={saving}
                  variant="outline"
                  className="flex-1 border-zinc-700 hover:bg-zinc-800 rounded-full"
                  data-testid="save-training-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Only'}
                </Button>
                <Button
                  onClick={() => handleSaveTraining(true)}
                  disabled={saving || regeneratingTraining}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                  data-testid="save-regenerate-training-btn"
                >
                  {regeneratingTraining ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Save & Generate New Program
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 text-center">
                Click &quot;Save & Generate&quot; to immediately create a new program with your updated preferences
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Goals Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="card-default mb-6"
        >
          <button
            onClick={() => setExpandedSection(expandedSection === 'goals' ? null : 'goals')}
            className="w-full flex items-center justify-between"
            data-testid="goals-prefs-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-[#F59E0B]" />
              </div>
              <div className="text-left">
                <h3 className="font-bold">Fitness Goals</h3>
                <p className="text-sm text-zinc-400">
                  {goalsPrefs.selected_goals.length > 0 
                    ? `${goalsPrefs.selected_goals.length} goal${goalsPrefs.selected_goals.length > 1 ? 's' : ''} selected`
                    : 'No goals selected'}
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${expandedSection === 'goals' ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSection === 'goals' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="mt-6 pt-6 border-t border-zinc-800 space-y-6"
            >
              <div>
                <Label className="text-zinc-300 mb-3 block">Select Your Goals</Label>
                <p className="text-xs text-zinc-500 mb-4">Choose the goals that matter most to you. Your training program will be tailored to these objectives.</p>
                <div className="grid grid-cols-2 gap-3">
                  {GOALS.filter(g => g.id !== 'custom').map((goal) => (
                    <button
                      key={goal.id}
                      onClick={() => toggleGoal(goal.id)}
                      className={`p-4 rounded-xl text-left transition-all ${
                        goalsPrefs.selected_goals.includes(goal.id)
                          ? 'bg-[#F59E0B]/20 border border-[#F59E0B]/30'
                          : 'bg-zinc-800 hover:bg-zinc-700 border border-transparent'
                      }`}
                    >
                      <p className="font-medium">{goal.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Custom Goal (Optional)</Label>
                <Input
                  value={goalsPrefs.custom_goal}
                  onChange={(e) => setGoalsPrefs({ ...goalsPrefs, custom_goal: e.target.value })}
                  placeholder="e.g., Complete my first triathlon, improve flexibility"
                  className="mt-2 bg-zinc-900 border-zinc-800"
                  data-testid="custom-goal-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleSaveGoals(false)}
                  disabled={saving}
                  variant="outline"
                  className="flex-1 border-zinc-700 hover:bg-zinc-800 rounded-full"
                  data-testid="save-goals-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Only'}
                </Button>
                <Button
                  onClick={() => handleSaveGoals(true)}
                  disabled={saving || regeneratingTraining}
                  className="flex-1 bg-[#F59E0B] text-black hover:bg-[#D97706] font-bold rounded-full"
                  data-testid="save-regenerate-goals-btn"
                >
                  {regeneratingTraining ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Save & Generate New Program
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 text-center">
                Click &quot;Save & Generate&quot; to immediately create a new program aligned with your goals
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Nutrition Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-default mb-6"
        >
          <button
            onClick={() => setExpandedSection(expandedSection === 'nutrition' ? null : 'nutrition')}
            className="w-full flex items-center justify-between"
            data-testid="nutrition-prefs-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center">
                <Utensils className="w-6 h-6 text-[#14B8A6]" />
              </div>
              <div className="text-left">
                <h3 className="font-bold">Nutrition Preferences</h3>
                <p className="text-sm text-zinc-400">
                  {nutritionPrefs.dietary_preference || 'No preference'} • {nutritionPrefs.meals_per_day} meals/day
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${expandedSection === 'nutrition' ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSection === 'nutrition' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="mt-6 pt-6 border-t border-zinc-800 space-y-6"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Dietary Preference</Label>
                  <Select
                    value={nutritionPrefs.dietary_preference}
                    onValueChange={(v) => setNutritionPrefs({ ...nutritionPrefs, dietary_preference: v })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIETARY_PREFERENCES.map(pref => (
                        <SelectItem key={pref} value={pref}>{pref}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Meals Per Day</Label>
                  <Select
                    value={String(nutritionPrefs.meals_per_day)}
                    onValueChange={(v) => setNutritionPrefs({ ...nutritionPrefs, meals_per_day: parseInt(v) })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2, 3, 4, 5, 6].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} meals</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 mb-3 block">Allergies</Label>
                <div className="flex flex-wrap gap-2">
                  {ALLERGIES.map(allergy => (
                    <button
                      key={allergy}
                      onClick={() => toggleAllergy(allergy)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        nutritionPrefs.allergies.includes(allergy)
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      {allergy}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 mb-3 block">Disliked Foods</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {nutritionPrefs.disliked_foods.map(food => (
                    <span
                      key={food}
                      className="px-3 py-1 rounded-full bg-zinc-800 text-sm flex items-center gap-2"
                    >
                      {food}
                      <button onClick={() => removeDislikedFood(food)} className="hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newDislikedFood}
                    onChange={(e) => setNewDislikedFood(e.target.value)}
                    placeholder="Add a food you dislike"
                    className="bg-zinc-900 border-zinc-800"
                    onKeyPress={(e) => e.key === 'Enter' && addDislikedFood()}
                    data-testid="disliked-food-input"
                  />
                  <Button onClick={addDislikedFood} variant="outline" className="border-zinc-700">
                    Add
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 mb-3 block">Disliked Ingredients</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {nutritionPrefs.disliked_ingredients.map(ing => (
                    <span
                      key={ing}
                      className="px-3 py-1 rounded-full bg-zinc-800 text-sm flex items-center gap-2"
                    >
                      {ing}
                      <button onClick={() => removeDislikedIngredient(ing)} className="hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newDislikedIngredient}
                    onChange={(e) => setNewDislikedIngredient(e.target.value)}
                    placeholder="Add an ingredient to exclude"
                    className="bg-zinc-900 border-zinc-800"
                    onKeyPress={(e) => e.key === 'Enter' && addDislikedIngredient()}
                    data-testid="disliked-ingredient-input"
                  />
                  <Button onClick={addDislikedIngredient} variant="outline" className="border-zinc-700">
                    Add
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Cooking Time</Label>
                  <Select
                    value={nutritionPrefs.cooking_time}
                    onValueChange={(v) => setNutritionPrefs({ ...nutritionPrefs, cooking_time: v })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimal (15 min)</SelectItem>
                      <SelectItem value="moderate">Moderate (30 min)</SelectItem>
                      <SelectItem value="extensive">Extensive (45+ min)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Budget</Label>
                  <Select
                    value={nutritionPrefs.budget}
                    onValueChange={(v) => setNutritionPrefs({ ...nutritionPrefs, budget: v })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="budget">Budget-friendly</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Cuisine Preferences */}
              <div className="pt-4 border-t border-zinc-800">
                <Label className="text-zinc-300 mb-3 block font-bold">Preferred Cuisines</Label>
                <p className="text-xs text-zinc-500 mb-3">Select the cuisine styles you enjoy for your meal plans</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {CUISINES.map(cuisine => (
                    <button
                      key={cuisine.id}
                      onClick={() => {
                        const cuisines = nutritionPrefs.preferred_cuisines || [];
                        if (cuisines.includes(cuisine.id)) {
                          setNutritionPrefs({ ...nutritionPrefs, preferred_cuisines: cuisines.filter(c => c !== cuisine.id) });
                        } else {
                          setNutritionPrefs({ ...nutritionPrefs, preferred_cuisines: [...cuisines, cuisine.id] });
                        }
                      }}
                      className={`p-3 rounded-lg text-left text-sm transition-all ${
                        (nutritionPrefs.preferred_cuisines || []).includes(cuisine.id)
                          ? 'bg-[#14B8A6]/20 border border-[#14B8A6]/30'
                          : 'bg-zinc-800 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="mr-2">{cuisine.flag}</span>
                      {cuisine.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleSaveNutrition(false)}
                  disabled={saving}
                  variant="outline"
                  className="flex-1 border-zinc-700 hover:bg-zinc-800 rounded-full"
                  data-testid="save-nutrition-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Only'}
                </Button>
                <Button
                  onClick={() => handleSaveNutrition(true)}
                  disabled={saving || regeneratingNutrition}
                  className="flex-1 bg-[#14B8A6] text-black hover:bg-[#0D9488] font-bold rounded-full"
                  data-testid="save-regenerate-nutrition-btn"
                >
                  {regeneratingNutrition ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Save & Generate New Plan
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 text-center">
                Click &quot;Save & Generate&quot; to immediately create a new meal plan with your updated preferences
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-default mb-6 cursor-pointer hover:border-zinc-700"
          onClick={() => navigate('/subscription')}
          data-testid="subscription-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-[#6366F1]" />
              </div>
              <div>
                <h3 className="font-bold">Subscription</h3>
                <p className="text-sm text-zinc-400">
                  {user?.subscription_status === 'active'
                    ? `${user.subscription_plan} plan`
                    : 'No active subscription'}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-500" />
          </div>
        </motion.div>

        {/* Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-default mb-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h3 className="font-bold">Notifications</h3>
              <p className="text-sm text-zinc-400">Manage your notification preferences</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-zinc-500">Receive updates via email</p>
              </div>
              <Switch
                checked={notifications.email}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })}
                data-testid="email-notifications"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-zinc-500">Receive updates via SMS</p>
              </div>
              <Switch
                checked={notifications.sms}
                onCheckedChange={(checked) => setNotifications({ ...notifications, sms: checked })}
                data-testid="sms-notifications"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Inactivity Reminders</p>
                <p className="text-sm text-zinc-500">Get notified after 48 hours of inactivity</p>
              </div>
              <Switch
                checked={notifications.inactivity}
                onCheckedChange={(checked) => setNotifications({ ...notifications, inactivity: checked })}
                data-testid="inactivity-notifications"
              />
            </div>
          </div>
        </motion.div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
